import styles from './LastMileStoresModalWindow.module.scss';
import Box from '@components/Box';
import ConfirmModal from '@components/modals/ConfirmModal';
import DelIcon from '@assets/icons/outline/trash-2.svg?react';
import FormErrorMessage from '@components/FormErrorMessage';
import LastMileWarningIcon from '@assets/icons/solid/circle-alert.svg?react';
import MarketplaceIcon from '@components/MarketplaceIcon';
import MenuItem from '@components/new/MenuItem';
import { LinkButton, LinkButtonSize } from '@components/new/LinkButton';
import PlacingOrderButtons from '@components/PlacingOrderButtons';
import PlacingOrderHeader from '@components/PlacingOrderHeader';
import SelectContainer from '@containers/SelectContainer';
import { ButtonType } from '@components/Button';
import { Column, Grid, Row } from '@components/Grid';
import {
  createLastMileYandexWarehouse,
  deleteLastMileWarehouse,
  getLastMileSellers,
  getLastMileWarehouses,
  TLastMileSeller,
  TLastMileWarehouse
} from '@api/apiClient/returns';
import { getErrorCode } from '@api/apiClient/sellerApiClient';
import { IconButton, IconButtonSize, IconButtonType } from '@components/new/IconButton';
import { ILastMileStoresModalWindowProps } from './types';
import { Text, TextColor, TextSize } from '@components/Text';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useKnowledgeBase } from '@contexts/KnowledgeBaseContext';
import { useProfile } from '@contexts/ProfileContext';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

enum EStep {
  LIST = 'list',
  ADD = 'add'
}

const LAST_MILE_INSTRUCTION_URL = 'https://unitrade-global.com/page139704916.html';

type TAddForm = {
  sellerId: string;
};

const LAST_MILE_DEPENDENT_QUERY_KEYS = [
  'awaiting',
  'resale',
  'lastMileResale',
  'processing',
  'returns',
  'disposal',
  'awaitingParcels',
  'getAutoResaleReturnsData'
];

const StoreSelectItem = ({ item, isSelected }: { item: TLastMileSeller, isSelected?: boolean }) => (
    <MenuItem
        title={item.name}
        active={isSelected}
    />
);

const StoreSelectValue = ({ item }: { item: TLastMileSeller | null }) => (<>{item?.name ?? ''}</>);

const LastMileStoresModalWindow: React.FC<ILastMileStoresModalWindowProps> = (props: ILastMileStoresModalWindowProps): React.ReactElement => {
  const { handlerSetIsOpen } = props;

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { get } = useProfile();
  const { setKnowledgeBaseParams } = useKnowledgeBase();
  const [step, setStep] = useState<EStep>(EStep.LIST);
  const [warehouseToDelete, setWarehouseToDelete] = useState<TLastMileWarehouse | null>(null);

  const invalidateLastMileDependentQueries = () => {
    LAST_MILE_DEPENDENT_QUERY_KEYS
        .forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
  };

  const { data: warehousesData, refetch: refetchWarehouses } = useQuery({
    queryKey: ['lastMileWarehouses'],
    queryFn: getLastMileWarehouses,
    retry: 1
  });

  const {
    data: sellersData,
    error: sellersError,
    isError: isSellersError,
    isFetched: isSellersFetched,
    isFetching: isSellersFetching
  } = useQuery({
    queryKey: ['lastMileSellers', 'Yandex'],
    queryFn: () => getLastMileSellers('Yandex'),
    retry: 1,
    enabled: step === EStep.ADD
  });

  const {
    control,
    handleSubmit,
    watch,
    reset
  } = useForm<TAddForm>({
    defaultValues: {
      sellerId: ''
    },
    mode: 'onChange'
  });

  const sellerId = watch('sellerId');
  const stores: TLastMileSeller[] = sellersData?.items ?? [];

  const {
    error: createError,
    isError: isCreateError,
    isLoading: isCreateLoading,
    mutate: mutateCreate
  } = useMutation({
    mutationFn: createLastMileYandexWarehouse,
    onSuccess: () => {
      refetchWarehouses();
      invalidateLastMileDependentQueries();
      get();
      window.setTimeout(() => get(), 1500);
      reset();
      setStep(EStep.LIST);
    }
  });

  const onSubmit = (form: TAddForm) => {
    mutateCreate({
      sellerId: form.sellerId
    });
  };

  const isAlreadyExistsError = isCreateError && getErrorCode(createError) === 'LastMileWarehouseAlreadyExists';
  const hasNoAvailableStores = isSellersFetched && !isSellersFetching && !isSellersError && !stores.length;

  const onDeleteSuccess = () => {
    toast.success(t('returnsPage.lastMileStoresModal.deleteSuccess'));
    refetchWarehouses();
    invalidateLastMileDependentQueries();
    get();
    window.setTimeout(() => get(), 1500);
  };

  const goToList = () => {
    reset();
    setStep(EStep.LIST);
  };

  if (step === EStep.ADD) {
    return (
        <Grid>
          <PlacingOrderHeader title={t('returnsPage.lastMileStoresModal.addTitle')}/>
          <Box mb={5} flex="column">
            <form onSubmit={handleSubmit(onSubmit)}>
              <SelectContainer<TLastMileSeller, TAddForm>
                  control={control}
                  name="sellerId"
                  title={t('returnsPage.lastMileStoresModal.storeInput')}
                  placeholder={t('returnsPage.lastMileStoresModal.storePlaceholder')}
                  renderSelectedItem={StoreSelectValue}
                  items={stores}
                  keyExtractor={(item: TLastMileSeller) => item.id}
                  ItemComponent={StoreSelectItem}
                  rules={{ required: t('defaultFormErrors.required') }}
                  disabled={isSellersError}
                  isLoading={isSellersFetching}
              />
            </form>
            {hasNoAvailableStores ? (
                <Box mt={3}>
                  <Text
                      color={TextColor.DARK_MUTED}
                      size={TextSize.S}
                  >
                    {t('returnsPage.lastMileStoresModal.noAvailableStores')}
                  </Text>
                </Box>
            ) : isAlreadyExistsError ? (
                <Box mt={3}>
                  <Text
                      color={TextColor.ERROR_DARK}
                      size={TextSize.S}
                  >
                    {t('returnsPage.lastMileStoresModal.alreadyExists')}
                  </Text>
                </Box>
            ) : (
                (isCreateError && <FormErrorMessage error={createError}/>)
                || (isSellersError && <FormErrorMessage error={sellersError}/>)
            )}
          </Box>
          <PlacingOrderButtons
              buttons={[
                {
                  action: goToList,
                  disabled: isCreateLoading,
                  text: t('returnsPage.lastMileStoresModal.back'),
                  type: ButtonType.SKELETON
                }, {
                  action: handleSubmit(onSubmit),
                  disabled: !sellerId || isSellersFetching || isCreateLoading,
                  pending: isCreateLoading,
                  text: t('returnsPage.lastMileStoresModal.add'),
                  type: ButtonType.BLOCK
                }
              ]}
          />
        </Grid>
    );
  }

  const items: TLastMileWarehouse[] = warehousesData?.warehouses ?? [];

  return (
      <Grid>
        <PlacingOrderHeader title={t('returnsPage.lastMileStoresModal.title')}/>
        <Row>
          <Column phone={12}>
            <Box mb={5}>
              <div className={styles.info}>
                <LastMileWarningIcon className={styles.infoIcon}/>
                <div className={styles.infoContent}>
                  <ul className={styles.infoList}>
                    <li>
                      <Text size={TextSize.S}>
                        {t('returnsPage.lastMileStoresModal.infoSales')}
                      </Text>
                    </li>
                    {items.map((warehouse: TLastMileWarehouse) => (
                        <li key={warehouse.id}>
                          <Text size={TextSize.S}>
                            {t('returnsPage.lastMileStoresModal.dropOffPoint', { warehouseName: warehouse.name })}
                          </Text>
                        </li>
                    ))}
                  </ul>
                  <LinkButton
                      className={styles.instructionLink}
                      onClick={() => setKnowledgeBaseParams(LAST_MILE_INSTRUCTION_URL)}
                      size={LinkButtonSize.S}
                      title={t('returnsPage.lastMileStoresModal.instruction')}
                  />
                </div>
              </div>
            </Box>
          </Column>
        </Row>
        <Row>
          <Column phone={12}>
            <Box mb={5}>
              {items.length ? (
                  <div className={styles.list}>
                    {items.map((store: TLastMileWarehouse) => (
                        <div
                            className={styles.storeRow}
                            key={store.id}
                        >
                          <div className={styles.storeLeft}>
                            <MarketplaceIcon marketplace={store.type}/>
                            <Text size={TextSize.S}>{store.name || store.externalId}</Text>
                          </div>
                          <IconButton
                              icon={DelIcon}
                              onClick={() => setWarehouseToDelete(store)}
                              size={IconButtonSize.L}
                              type={IconButtonType.ICON}
                          />
                        </div>
                    ))}
                  </div>
              ) : (
                  <Text
                      color={TextColor.DARK_MUTED}
                      size={TextSize.S}
                  >
                    {t('returnsPage.lastMileStoresModal.empty')}
                  </Text>
              )}
            </Box>
          </Column>
        </Row>
        <PlacingOrderButtons
            buttons={[
              {
                action: () => handlerSetIsOpen(false),
                text: t('returnsPage.lastMileStoresModal.cancel'),
                type: ButtonType.SKELETON
              }, {
                action: () => setStep(EStep.ADD),
                text: t('returnsPage.lastMileStoresModal.addYmStore'),
                type: ButtonType.BLOCK
              }
            ]}
        />
        {warehouseToDelete && (
            <ConfirmModal
                title={t('returnsPage.lastMileStoresModal.deleteTitle', { name: warehouseToDelete.name || warehouseToDelete.externalId })}
                description={t('returnsPage.lastMileStoresModal.deleteDescription')}
                yesBtnTitle={t('returnsPage.lastMileStoresModal.deleteBtn')}
                noBtnTitle={t('returnsPage.lastMileStoresModal.cancel')}
                icon={<DelIcon/>}
                onClose={() => setWarehouseToDelete(null)}
                onSuccess={onDeleteSuccess}
                requestFn={() => deleteLastMileWarehouse(warehouseToDelete.id).catch((error) => {
                  if (getErrorCode(error) === 'LastMileWarehouseNotFound') return;
                  throw error;
                })}
            />
        )}
      </Grid>
  );
};

export default LastMileStoresModalWindow;
